import { z } from 'zod';
import { idSchema } from './commun.js';
export const TYPES_CAISSE = ['caisse_bois', 'carton', 'valise', 'portfolio'];
export const ETATS_CAISSE = ['vide', 'pleine', 'endommagee', 'reformee'];
export const TYPES_CIBLE_ETIQUETTE = ['oeuvre', 'exemplaire', 'emplacement', 'caisse'];
export const FORMATS_ETIQUETTE = [
    'rouleau-62x29',
    'planche-a4-24',
    'cartel-10x15',
    'cartel-a6',
    'etiquette-100x50',
    'cartel-riche-a6',
];
/** Le cartel mural : son QR mène à la fiche publique de l'œuvre, pas à la régie. */
export const FORMATS_CARTEL = ['cartel-10x15', 'cartel-a6', 'cartel-riche-a6'];
/**
 * Le cartel riche (ARTO-200) : image, mentions de la fiche, description courte
 * et QR. Chaque bloc se coupe ; tout est affiché tant qu'on ne dit rien.
 */
export const BLOCS_CARTEL_RICHE = ['image', 'description', 'edition', 'inventaire'];
export const GESTES_RESOLUTION = ['deplacer_ici', 'introuvable', 'ignorer'];
export const PREFIXE_QR = 'ART:';
export function estFormatCartel(format) {
    return FORMATS_CARTEL.includes(format);
}
const JETON_ETIQUETTE = /^[0-9A-HJKMNP-TV-Z]{20}$/;
/** Ce qu'encode le QR d'une étiquette interne : la régie l'ouvre, connexion requise. */
export function adresseEtiquette(appUrl, jeton) {
    return `${appUrl.replace(/\/+$/, '')}/e/${jeton}`;
}
/**
 * Le lecteur rend uniquement un jeton Crockford opaque, jamais une URL ou un
 * identifiant. Il reconnaît l'adresse `…/e/<jeton>` des étiquettes récentes,
 * quel que soit l'hôte, et `ART:<jeton>` des premières étiquettes.
 */
export function analyserCodeEtiquette(texte) {
    const adresse = /^https?:\/\/[^/\s]+\/e\/([^/?#\s]+)\/?(?:[?#]\S*)?$/i.exec(texte.trim());
    const code = (adresse ? adresse[1] : texte).replace(/\s/g, '').toUpperCase();
    const jeton = !adresse && code.startsWith(PREFIXE_QR) ? code.slice(PREFIXE_QR.length) : code;
    return JETON_ETIQUETTE.test(jeton) ? jeton : null;
}
export const dimensionsCaisseSchema = z.strictObject({
    hauteur: z.number().positive(),
    largeur: z.number().positive(),
    profondeur: z.number().positive(),
});
export const creationEmplacementSchema = z.strictObject({
    lieuId: idSchema,
    parentId: idSchema.nullish(),
    nom: z.string().trim().min(1).max(120),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9-]{1,16}$/, 'Code attendu : lettres, chiffres et tirets, au plus 16 caractères.'),
    actif: z.boolean().optional(),
    notes: z.string().trim().max(5000).optional(),
});
export const modificationEmplacementSchema = creationEmplacementSchema.partial();
export const destinationReserveSchema = z.strictObject({
    lieuId: idSchema.nullable(),
    emplacementId: idSchema.nullish(),
});
export const creationCaisseSchema = z.strictObject({
    type: z.enum(TYPES_CAISSE),
    dimensionsInterieures: dimensionsCaisseSchema.nullish(),
    dimensionsExterieures: dimensionsCaisseSchema.nullish(),
    etat: z.enum(ETATS_CAISSE).optional(),
    lieuId: idSchema.nullish(),
    emplacementId: idSchema.nullish(),
    mediaIds: z.array(idSchema).max(100).optional(),
    notes: z.string().trim().max(5000).optional(),
});
export const revisionReserveSchema = z.int().min(0);
export const modificationCaisseSchema = creationCaisseSchema
    .omit({ lieuId: true, emplacementId: true })
    .partial()
    .extend({ revision: revisionReserveSchema });
export const ajoutContenuCaisseSchema = z.strictObject({
    oeuvreId: idSchema,
    exemplaireId: idSchema.nullish(),
    revision: revisionReserveSchema,
});
export const retraitContenuCaisseSchema = z.strictObject({ revision: revisionReserveSchema });
export const deplacementCaisseSchema = destinationReserveSchema.extend({
    revision: revisionReserveSchema,
});
export const caissesMouvementSchema = z.strictObject({ caisseIds: z.array(idSchema).max(100) });
export const cibleEtiquetteSchema = z.strictObject({
    type: z.enum(TYPES_CIBLE_ETIQUETTE),
    id: idSchema,
});
export const resolutionEtiquetteSchema = z.strictObject({
    code: z.string().trim().min(1).max(200),
});
export const creationEtiquettesSchema = z.strictObject({
    cibles: z.array(cibleEtiquetteSchema).min(1).max(200),
});
export const regenerationEtiquetteSchema = z.strictObject({ cible: cibleEtiquetteSchema });
export const pdfEtiquettesSchema = creationEtiquettesSchema.extend({
    format: z.enum(FORMATS_ETIQUETTE),
    texteLibre: z.string().trim().max(200).optional(),
    /** Cartel seulement : le prix ne paraît sur la fiche publique que si on le demande. */
    afficherPrix: z.boolean().optional(),
    /** Cartel riche seulement : les blocs à couper (tous présents par défaut). */
    riche: z.strictObject({ sans: z.array(z.enum(BLOCS_CARTEL_RICHE)).max(4) }).optional(),
});
export const revocationCartelSchema = z.strictObject({ oeuvreId: idSchema });
export const perimetreInventaireSchema = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('lieu'), lieuId: idSchema }),
    z.strictObject({ type: z.literal('emplacement'), emplacementId: idSchema }),
    z.strictObject({ type: z.literal('caisses'), caisseIds: z.array(idSchema).min(1).max(100) }),
]);
export const ouvertureInventaireSchema = z.strictObject({ perimetre: perimetreInventaireSchema });
export const scansInventaireSchema = z.strictObject({
    scans: z
        .array(z.strictObject({
        cleIdempotence: z.uuid(),
        code: z.string().min(1).max(200),
        scanneLe: z.iso.datetime({ offset: true }),
        appareil: z.string().trim().max(80).optional(),
    }))
        .min(1)
        .max(200),
});
export const resolutionInventaireSchema = z.strictObject({
    cle: z.string().min(1).max(80),
    geste: z.enum(GESTES_RESOLUTION),
    note: z.string().trim().max(5000).optional(),
    revision: revisionReserveSchema,
});
export const clotureInventaireSchema = z.strictObject({ revision: revisionReserveSchema });
export const deplacementsInventaireSchema = z.strictObject({
    cleIdempotence: z.uuid(),
    destination: z.union([destinationReserveSchema, z.strictObject({ caisseId: idSchema })]),
    cibles: z
        .array(z.strictObject({ type: z.enum(['oeuvre', 'exemplaire']), id: idSchema }))
        .min(1)
        .max(100),
});
//# sourceMappingURL=reserve.js.map